import { Button, Typography, Alert, Radio, Space, message } from 'antd'
import { useHideModal } from 'components/Modals'
import { Trans } from 'react-i18next'
import { useLocalization } from 'localization'
import Container from 'components/Container'
import { IGetTransactionListParams, useIntegrations, useWealthicaSyncIntegrations } from 'api'
import { useAppContext } from 'modules/common/appContext'
import WealthicaAPI from 'api/wealthica/services'
import appRoutes from 'modules/common/app-routes'
import { useCallback, useEffect, useMemo, useState } from 'react'

import Styled from './styled'
import { useShowModal } from 'modules/common/GlobalModals'
import { getTransactions } from 'api/transactions/services'
import { exportTransactionsToCSV } from 'modules/transactions/TransactionsPageContent/helpers'
import { useNewWealthicaTransactions } from 'api/wealthica/queries'

type SyncMethodType = 'append' | 'override'

const WealthicaTab = () => {
	const { t } = useLocalization()
	const hideModal = useHideModal()
	const { isWealthicaContext } = useAppContext()
	const { data: integrations = [] } = useIntegrations()
	const showModal = useShowModal()

	const wealthicaIntegration = useMemo(() => integrations.find(item => item.sourceType === 'WEALTHICA'), [integrations])
	const { data: wealthicaTransactionData } = useNewWealthicaTransactions(wealthicaIntegration?.lastTransactionId, {
		enabled: isWealthicaContext,
	})

	const syncMethods = useMemo<{ value: SyncMethodType; label: string }[]>(
		() => [
			{
				value: 'append',
				label: t('integrations.wealthica.methods.append'),
			},
			{
				value: 'override',
				label: t('integrations.wealthica.methods.override'),
			},
		],
		[t]
	)

	const [selectedMethod, selectMethod] = useState<SyncMethodType>(syncMethods[0].value)

	const { mutateAsync: syncWealthica, isLoading } = useWealthicaSyncIntegrations()
	const [isWealthicaLoading, setIsWealthicaLoading] = useState(false)

	useEffect(() => {
		selectMethod(syncMethods[wealthicaTransactionData?.vezgoTransactions.length ? 0 : 1].value)
	}, [wealthicaTransactionData?.vezgoTransactions.length])

	const onExport = useCallback(async () => {
		const paramsForExport: IGetTransactionListParams = {
			page: 0,
			size: 99999, // pull all using filter
		}
		try {
			const { items = [] } = await getTransactions(paramsForExport)
			exportTransactionsToCSV(items)
		} catch (ex) {
			message.error(t('tranactions.export.error'))
		}
	}, [t, getTransactions])

	const onSync = async () => {
		if (selectedMethod === 'append' && wealthicaIntegration && !wealthicaIntegration?.lastTransactionId) {
			message.error(t('integrations.wealthica.sync.noLastTransaction'))
			return
		}

		setIsWealthicaLoading(true)

		const { transactions, vezgoTransactions } =
			selectedMethod === 'append' && wealthicaIntegration?.lastTransactionId && wealthicaTransactionData
				? wealthicaTransactionData
				: await WealthicaAPI.getAllCryptoTransactions()

		setIsWealthicaLoading(false)
		if (transactions) {
			if (selectedMethod === 'append') {
				await syncWealthica({
					mode: 'SYNC_METHOD_APPEND',
					transactions,
					vezgoTransactions,
				})
			} else {
				showModal('wealthica.resync', {
					title: t('integrations.wealthica.syncOverride.title'),
					subtitle: (
						<Alert
							type="warning"
							showIcon
							message={
								<Typography.Text>
									<Trans key="integrations.wealthica.syncOverride.subtitle">
										You are about to resync all your Wealthica transactions. This will remove all your previously
										synchronized transactions. Before proceeding, please consider using{' '}
										<Typography.Link
											underline
											href=""
											onClick={e => {
												e.preventDefault()
												onExport()
											}}
										>
											export
										</Typography.Link>{' '}
										to backup your current BlockSentry data.
									</Trans>
								</Typography.Text>
							}
						/>
					),
					hardConfirmWord: t('actions.resync'),
					submit: () => {
						return syncWealthica({
							mode: 'SYNC_METHOD_OVERRIDE',
							transactions,
							vezgoTransactions,
						})
					},
					confirmLoading: isLoading,
				})
				return
			}
		}
		hideModal()
	}

	return (
		<Container display="flex" flexDirection="column" space="md" spaceDirection="vertical">
			{isWealthicaContext ? (
				<Container space="md" spaceDirection="vertical">
					{!!wealthicaTransactionData?.vezgoTransactions.length && (
						<Alert
							type="success"
							message={
								wealthicaTransactionData.vezgoTransactions.length === 1
									? t('integrations.wealthica.sync.newTransaction')
									: t('integrations.wealthica.sync.newTransactions', {
											amount: wealthicaTransactionData?.vezgoTransactions.length,
									  })
							}
						/>
					)}
					<Styled.SyncOptions onChange={e => selectMethod(e.target.value)} value={selectedMethod}>
						<Space direction="vertical" size="middle">
							{syncMethods.map(({ value, label }) => (
								<Radio
									key={value}
									value={value}
									disabled={
										value === 'append' &&
										(!wealthicaTransactionData?.vezgoTransactions.length ||
											(wealthicaIntegration && !wealthicaIntegration.lastTransactionId))
									}
								>
									{label}
								</Radio>
							))}
						</Space>
					</Styled.SyncOptions>
					{selectedMethod === 'override' && (
						<Alert
							message={<Typography.Text>{t('integrations.wealthica.sync.title')}</Typography.Text>}
							type="warning"
						/>
					)}
					<Button
						type="primary"
						onClick={onSync}
						disabled={!isWealthicaContext}
						loading={isLoading || isWealthicaLoading}
					>
						{t('actions.resync')}
					</Button>
				</Container>
			) : (
				<Alert
					message={
						<Typography.Text>
							<Trans key="integrations.wealthica.sync.noContext">
								Wealthica synchronization is only possible within the addon. Please visit{' '}
								<Typography.Link underline href={appRoutes.external.wealthica} target="_blank">
									https://app.wealthica.com/
								</Typography.Link>{' '}
								to synchronize your Wealthica transactions.
							</Trans>
						</Typography.Text>
					}
					type="error"
				/>
			)}
		</Container>
	)
}

export default WealthicaTab
